import { Sparkles, Heart, ShieldCheck, Recycle, Users, Star } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";

const VALUES = [
  { icon: ShieldCheck, title: "Verified & Authentic", text: "Every seller is manually approved before they can list, and each purchase is escrow-protected — so what you see is what you get." },
  { icon: Heart, title: "By the community", text: "Real reviews, real people and real beauty finds. Buyers rate every sale with a 5-star system so trust is built into the club." },
  { icon: Recycle, title: "Beauty, less waste", text: "That barely-used palette or the lipstick that wasn't your shade deserves a second life. Reselling beauty is kinder to your wallet and the planet." },
  { icon: Star, title: "Influencer picks", text: "Discover products curated by verified beauty creators — and if you're an influencer, earn commission on the finds you recommend." },
];

export default function About() {
  const navigate = useNavigate();
  usePageMeta(
    "About Us — The Beauty Marketplace | Beauty Trade Club",
    "Beauty Trade Club is the community marketplace to buy, sell and trade new & gently-used makeup, skincare and fragrance from verified sellers. Discover our story.",
    "/about"
  );
  return (
    <div className="max-w-3xl mx-auto px-6 py-16">
      <div className="text-center mb-12">
        <Sparkles className="text-[#E8956B] mx-auto mb-4" size={32} />
        <h1 className="font-display text-4xl sm:text-5xl font-black">About Beauty Trade Club</h1>
        <p className="text-neutral-500 mt-4 text-lg">The trusted community marketplace for new & gently-used beauty.</p>
      </div>

      <div className="space-y-6 text-neutral-600 leading-relaxed text-lg">
        <p>
          Beauty Trade Club (BTC) is the community-driven marketplace for beauty lovers to
          <span className="text-neutral-900 font-semibold"> buy, sell and trade </span>
          their favorite new and gently-used makeup, skincare and fragrance. We built BTC for the millions of
          people who have a drawer full of beauty products they no longer use — and for the shoppers hunting for
          their next holy-grail find at a fraction of the retail price.
        </p>
        <p>
          We believe great beauty shouldn't go to waste. That barely-used palette, the foundation that wasn't
          quite your shade, the perfume you fell out of love with — someone out there wants it. Instead of letting
          products gather dust or end up in landfill, BTC connects buyers and sellers in a trusted, verified space
          where every transaction is protected.
        </p>
        <h2 className="font-display text-2xl font-bold text-neutral-900 pt-2">Our mission</h2>
        <p>
          Our mission is simple: make secondhand and surplus beauty <span className="font-semibold text-neutral-900">safe,
          social and sustainable</span>. We manually verify every seller, hold each payment in escrow until the buyer
          confirms their item arrived as described, and give the whole community a voice through honest star reviews.
          The result is a marketplace where you can shop — and sell — with total confidence.
        </p>
        <h2 className="font-display text-2xl font-bold text-neutral-900 pt-2">How BTC protects you</h2>
        <p>
          When you buy on Beauty Trade Club, your payment is held securely and only released to the seller after you
          confirm the item arrived as described (or seven days after it ships). If something isn't right, you can
          report an issue and our team steps in before any money changes hands. Sellers keep 85% of every sale, and
          our small platform fee funds the verification, escrow protection and support that keep the club trustworthy.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-6 mt-12">
        {VALUES.map((v) => (
          <div key={v.title} className="bg-white border border-neutral-200 rounded-2xl p-6">
            <v.icon className="text-[#E8956B] mb-3" />
            <h3 className="font-bold text-lg mb-1">{v.title}</h3>
            <p className="text-sm text-neutral-500">{v.text}</p>
          </div>
        ))}
      </div>

      <div className="mt-14 text-center bg-[#E8956B]/10 border border-[#E8956B]/25 rounded-3xl p-10">
        <Users className="text-[#E8956B] mx-auto mb-3" />
        <h2 className="font-display text-2xl font-bold">Join the club</h2>
        <p className="text-neutral-600 mt-3 max-w-xl mx-auto">
          Whether you're clearing out your vanity or hunting for a deal, there's a place for you at Beauty Trade Club.
          Start shopping today, or apply to become a verified seller and turn your unused beauty into cash.
        </p>
        <div className="flex flex-wrap justify-center gap-3 mt-6">
          <button data-testid="about-shop-btn" onClick={() => navigate("/shop")} className="bg-[#E8956B] hover:bg-[#CE744A] text-white font-bold rounded-full px-7 py-3 btn-glow transition-all">
            Shop the Club
          </button>
          <button data-testid="about-sell-btn" onClick={() => navigate("/sell")} className="border-2 border-[#E8956B] text-[#E8956B] hover:bg-[#E8956B] hover:text-white font-bold rounded-full px-7 py-3 transition-all">
            Start Selling
          </button>
        </div>
      </div>
    </div>
  );
}
